<template>
  <PublicLayout page-title="Centro de Ayuda">
    <!-- Hero Section -->
    <section class="relative py-24 overflow-hidden">
      <!-- Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-accent-50/20" />
      <div class="absolute top-0 right-0 w-96 h-96 bg-accent-200/20 rounded-full blur-3xl" />
      <div class="absolute bottom-0 left-0 w-64 h-64 bg-teal-200/15 rounded-full blur-3xl" />

      <div class="relative max-w-4xl mx-auto px-6 text-center">
        <!-- Icon -->
        <div class="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-accent-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg shadow-accent-500/25 animate-fade-up">
          <QuestionMarkCircleIcon class="w-10 h-10 text-white" />
        </div>

        <!-- Title -->
        <h1 class="text-5xl md:text-6xl font-bold text-text-primary tracking-tight mb-4 animate-fade-up delay-100">
          ¿Cómo podemos ayudarte?
        </h1>

        <p class="text-xl text-text-secondary mb-8 animate-fade-up delay-200">
          Encuentra respuestas rápidas o contacta con nuestro equipo
        </p>

        <!-- Search Box -->
        <div class="max-w-2xl mx-auto animate-fade-up delay-300">
          <div class="relative">
            <MagnifyingGlassIcon class="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Buscar en el centro de ayuda..."
              class="w-full pl-14 pr-6 py-5 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-2xl text-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 shadow-xl shadow-slate-100/50 transition-all"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Help Categories -->
    <section class="py-24">
      <div class="max-w-6xl mx-auto px-6">
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary text-center mb-12">
          Explora por categoría
        </h2>

        <div class="grid md:grid-cols-3 gap-6">
          <RouterLink
            v-for="(cat, index) in helpCategories"
            :key="cat.slug"
            :to="cat.link"
            class="group bg-white rounded-2xl p-8 border border-slate-200 shadow-lg hover:shadow-xl hover:border-accent-300 transition-all duration-300 text-center animate-fade-up"
            :style="{ animationDelay: `${index * 100}ms` }"
          >
            <div class="w-16 h-16 mx-auto mb-4 bg-accent-50 rounded-2xl flex items-center justify-center group-hover:bg-accent-100 group-hover:scale-110 transition-all duration-300">
              <component :is="cat.icon" class="w-8 h-8 text-accent-600" />
            </div>
            <h3 class="text-xl font-bold text-text-primary mb-2 group-hover:text-accent-600 transition-colors">
              {{ cat.name }}
            </h3>
            <p class="text-text-secondary text-sm mb-4">
              {{ cat.description }}
            </p>
            <span class="inline-flex items-center text-accent-600 text-sm font-medium">
              Ver artículos
              <ArrowRightIcon class="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </span>
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-24 bg-slate-50/50">
      <div class="max-w-3xl mx-auto px-6">
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary text-center mb-4">
          Preguntas frecuentes
        </h2>
        <p class="text-text-secondary text-center mb-12">
          Las dudas más comunes de nuestros usuarios
        </p>

        <div class="space-y-4">
          <FAQItem
            v-for="(faq, index) in filteredFaqs"
            :key="index"
            :question="faq.question"
            :answer="faq.answer"
          />
        </div>

        <div v-if="searchQuery && filteredFaqs.length === 0" class="text-center py-12">
          <div class="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
            <MagnifyingGlassIcon class="w-8 h-8 text-slate-400" />
          </div>
          <p class="text-text-secondary">
            No se encontraron resultados para "{{ searchQuery }}"
          </p>
        </div>
      </div>
    </section>

    <!-- Contact Hours Section -->
    <section class="py-16">
      <div class="max-w-4xl mx-auto px-6">
        <div class="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary text-center mb-8">
            Horarios de Atención
          </h2>

          <div class="grid md:grid-cols-3 gap-6">
            <div class="text-center p-6 bg-slate-50 rounded-2xl">
              <div class="w-12 h-12 mx-auto mb-4 bg-accent-100 rounded-xl flex items-center justify-center">
                <ClockIcon class="w-6 h-6 text-accent-600" />
              </div>
              <h3 class="font-bold text-text-primary mb-2">Soporte General</h3>
              <p class="text-text-secondary text-sm">Lunes a Viernes: 9:00 - 18:00</p>
              <p class="text-text-secondary text-sm">Sábados: 10:00 - 14:00</p>
            </div>

            <div class="text-center p-6 bg-slate-50 rounded-2xl">
              <div class="w-12 h-12 mx-auto mb-4 bg-accent-100 rounded-xl flex items-center justify-center">
                <ScaleIcon class="w-6 h-6 text-accent-600" />
              </div>
              <h3 class="font-bold text-text-primary mb-2">Consultas Legales</h3>
              <p class="text-text-secondary text-sm">Lunes a Viernes: 10:00 - 17:00</p>
              <p class="text-text-secondary text-sm">(Solo con cita previa)</p>
            </div>

            <div class="text-center p-6 bg-slate-50 rounded-2xl">
              <div class="w-12 h-12 mx-auto mb-4 bg-accent-100 rounded-xl flex items-center justify-center">
                <BoltIcon class="w-6 h-6 text-accent-600" />
              </div>
              <h3 class="font-bold text-text-primary mb-2">Emergencias</h3>
              <p class="text-text-secondary text-sm">24/7 vía email</p>
              <p class="text-text-secondary text-sm">Respuesta en 4-6 horas</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-24">
      <div class="max-w-4xl mx-auto px-6">
        <GradientCTA
          title="¿No encuentras lo que buscas?"
          description="Nuestro equipo está listo para ayudarte personalmente con cualquier consulta"
        >
          <RouterLink
            to="/support/contact"
            class="inline-flex items-center gap-2 px-8 py-4 bg-white text-accent-600 font-bold rounded-full hover:bg-white/90 transition-colors shadow-lg hover:shadow-xl"
          >
            <ChatBubbleLeftRightIcon class="w-5 h-5" />
            Contactar con soporte
          </RouterLink>
        </GradientCTA>
      </div>
    </section>
  </PublicLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useSeo } from '@/composables/useSeo'
import { useSchemaOrg, createFAQSchema } from '@/composables/useSchemaOrg'
import PublicLayout from '@/components/layout/PublicLayout.vue'

// SEO Meta Tags
useSeo({
  title: 'Centro de Ayuda',
  description: 'Preguntas frecuentes sobre derecho al olvido, plazos, costes y proceso legal.',
  canonical: '/centro-ayuda'
})

// Schema.org FAQPage (SEO Phase 4)
const schemaFaqs = [
  {
    question: '¿Qué es el derecho al olvido según GDPR?',
    answer: 'El derecho al olvido es el Artículo 17 del GDPR. Te permite solicitar que se elimine tu información personal de los sistemas de las organizaciones cuando ya no es necesaria, has retirado el consentimiento, o tienes razones legales para pedir su eliminación.'
  },
  {
    question: '¿Cuánto cuesta el servicio de Derecho al Olvido?',
    answer: 'El servicio de Derecho al Olvido tiene un precio fijo de 499€. Incluye análisis legal completo, redacción de documentos GDPR profesionales, gestión de solicitudes hasta 5 URLs y seguimiento del caso hasta conseguir la eliminación.'
  },
  {
    question: '¿Cuánto tiempo tarda el proceso de eliminación?',
    answer: 'El proceso típico dura entre 2 y 4 semanas, dependiendo del número de plataformas donde aparecen tus datos, complejidad del caso y velocidad de respuesta de cada plataforma.'
  },
  {
    question: '¿Qué diferencia hay entre Derecho al Olvido y Post-Mortem?',
    answer: 'Derecho al Olvido (499€) es para personas vivas que desean eliminar su información. Derecho al Olvido Post-Mortem (699€) es para familiares que gestionan el legado digital de una persona fallecida.'
  },
  {
    question: '¿Es confidencial mi información?',
    answer: 'Sí. Todos tus datos están encriptados a nivel de campo con AES-256-GCM. Cumplimos 100% con GDPR, incluyendo derecho de acceso, rectificación y supresión. Tu información nunca se comparte con terceros.'
  }
]
useSchemaOrg(createFAQSchema(schemaFaqs))
import GradientCTA from '@/components/ui/GradientCTA.vue'
import FAQItem from '@/components/ui/FAQItem.vue'
import {
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ScaleIcon,
  CreditCardIcon,
  CogIcon,
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  BoltIcon
} from '@heroicons/vue/24/outline'

// Search
const searchQuery = ref('')

// Help categories
const helpCategories = [
  {
    name: 'Derecho al Olvido',
    slug: 'derecho-olvido',
    description: 'Guías sobre cómo ejercer tu derecho a eliminar información',
    icon: ShieldCheckIcon,
    link: '/services/derecho-olvido'
  },
  {
    name: 'Derecho al Olvido Post-Mortem',
    slug: 'post-mortem',
    description: 'Eliminación de datos personales de buscadores y plataformas de personas fallecidas',
    icon: DocumentTextIcon,
    link: '/services/derecho-olvido-postmortem'
  },
  {
    name: 'Testamento Digital',
    slug: 'testamento',
    description: 'Planificación de tu legado digital',
    icon: ScaleIcon,
    link: '/services/testamento-digital'
  },
  {
    name: 'Facturación',
    slug: 'facturacion',
    description: 'Pagos, facturas y métodos de pago',
    icon: CreditCardIcon,
    link: '/pricing'
  },
  {
    name: 'Soporte Técnico',
    slug: 'soporte',
    description: 'Problemas técnicos y errores',
    icon: CogIcon,
    link: '/support/contact'
  }
]

// FAQs
const faqs = [
  {
    question: '¿Qué es el derecho al olvido?',
    answer: 'El derecho al olvido es un derecho fundamental que permite a las personas solicitar la eliminación de información personal de internet cuando esta sea inexacta, inadecuada o excesiva. Está regulado por el GDPR (Reglamento General de Protección de Datos) y permite a los ciudadanos europeos controlar su huella digital.'
  },
  {
    question: '¿Cuánto tiempo toma el proceso?',
    answer: 'Los tiempos varían según la complejidad del caso y la colaboración de las plataformas. Generalmente entre 30-90 días para casos estándar. En nuestro servicio Testamento Digital, garantizamos tiempos de respuesta más rápidos y seguimiento personalizado.'
  },
  {
    question: '¿Ofrecen garantías?',
    answer: 'Garantizamos profesionalidad y seguimiento completo del proceso. Los resultados dependen de factores legales y la cooperación de terceros.'
  },
  {
    question: '¿Qué documentos necesito?',
    answer: 'Para iniciar el proceso necesitarás tu DNI/NIE, documentos que prueben tu identidad, y evidencia de la información que quieres eliminar. En casos post-mortem, se requieren documentos adicionales como certificado de defunción y prueba de parentesco.'
  },
  {
    question: '¿Es legal eliminar información de internet?',
    answer: 'Sí, el derecho al olvido está garantizado por la legislación europea (GDPR). Sin embargo, existen excepciones como información de interés público, libertad de expresión, o datos necesarios para el cumplimiento de obligaciones legales.'
  },
  {
    question: '¿Qué plataformas cubren sus servicios?',
    answer: 'Trabajamos con todas las principales plataformas: Google, Facebook, Twitter, LinkedIn, Instagram, y buscadores web. Los servicios Derecho al Olvido y Post-Mortem permiten hasta 5 URLs por caso.'
  },
  {
    question: '¿Cómo puedo hacer seguimiento de mi caso?',
    answer: 'Una vez registrado, tendrás acceso a tu panel de control donde podrás ver el estado de todas tus solicitudes, documentos enviados y comunicaciones con nuestro equipo legal.'
  }
]

// Filtered FAQs based on search
const filteredFaqs = computed(() => {
  if (!searchQuery.value) return faqs

  const query = searchQuery.value.toLowerCase()
  return faqs.filter(faq =>
    faq.question.toLowerCase().includes(query) ||
    faq.answer.toLowerCase().includes(query)
  )
})

// Analytics
if (typeof window !== 'undefined' && window.gtag) {
  window.gtag('event', 'help_center_view', {
    event_category: 'support',
    event_label: 'help_center',
    page_title: 'Centro de Ayuda',
    value: 1
  })
}
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Focus states */
input:focus {
  outline: none;
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .animate-fade-up {
    animation: none;
  }

  .group-hover\:scale-110 {
    transform: none;
  }

  .group-hover\:translate-x-1 {
    transform: none;
  }
}
</style>
